import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, GripVertical, ImageUp, Plus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (u) => (u && u.startsWith("/api/") ? `${API}${u}` : u);
const SHAPES = [
  { value: "tall", label: "Tall (arch)" },
  { value: "wide", label: "Wide" },
  { value: "standard", label: "Standard" },
];
const selectCls =
  "bg-midnight border border-ivory/20 rounded-lg px-3 py-2 text-ivory text-sm outline-none focus:border-blush transition-colors duration-300";

const MAX_MB = 15;
const tooBig = (file) => file && file.size > MAX_MB * 1024 * 1024;

export default function GalleryManager({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState(null);
  const [newDesc, setNewDesc] = useState("");
  const [newShape, setNewShape] = useState("standard");
  const [adding, setAdding] = useState(false);
  const addFileRef = useRef(null);
  const dragIndex = useRef(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/api/gallery`);
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const addPhoto = async (e) => {
    e.preventDefault();
    const file = addFileRef.current?.files?.[0];
    if (!file) { toast.error("Please choose an image first."); return; }
    if (tooBig(file)) { toast.error(`Image is too large (max ${MAX_MB} MB).`); return; }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", newDesc);
      fd.append("shape", newShape);
      await axios.post(`${API}/api/admin/gallery`, fd, auth);
      setNewDesc("");
      setNewShape("standard");
      if (addFileRef.current) addFileRef.current.value = "";
      toast.success("Photo added to the gallery.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const saveField = async (id, patch) => {
    try {
      await axios.put(`${API}/api/admin/gallery/${id}`, patch, auth);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
      toast.success("Saved.");
    } catch {
      toast.error("Could not save the change.");
    }
  };

  const replaceImage = async (id, file) => {
    if (!file) return;
    if (tooBig(file)) { toast.error(`Image is too large (max ${MAX_MB} MB).`); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/api/admin/gallery/${id}/image`, fd, auth);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, image_url: data.image_url } : it)));
      toast.success("Photo replaced.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Replace failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery? It will disappear from the live site.")) return;
    try {
      await axios.delete(`${API}/api/admin/gallery/${id}`, auth);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Photo removed.");
    } catch {
      toast.error("Could not remove the photo.");
    }
  };

  const onDragOver = (e, i) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
  };

  const persistOrder = async () => {
    dragIndex.current = null;
    try {
      await axios.put(`${API}/api/admin/gallery/reorder`, { ids: items.map((it) => it.id) }, auth);
    } catch {
      toast.error("Could not save the new order.");
    }
  };

  const setLocalDesc = (id, value) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, description: value } : it)));

  return (
    <div data-testid="gallery-manager">
      <form
        data-testid="gallery-add-form"
        onSubmit={addPhoto}
        className="bg-ivory/5 border border-ivory/15 rounded-2xl p-6 sm:p-8 mb-10 grid gap-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
      >
        <div className="sm:col-span-3">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">Add a photo</p>
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.2em] uppercase text-ivory/50 mb-2">Image (JPG · PNG · WebP)</label>
          <input
            data-testid="gallery-add-file"
            ref={addFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="block w-full text-sm text-ivory/70 file:mr-4 file:rounded-full file:border-0 file:bg-blush file:text-midnight file:px-4 file:py-2 file:text-[11px] file:uppercase file:tracking-[0.2em] file:cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.2em] uppercase text-ivory/50 mb-2">Description</label>
          <input
            data-testid="gallery-add-desc"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="e.g. A Fantasy @ The Westin Hotel"
            className="w-full bg-transparent border border-ivory/20 rounded-lg px-3 py-2 text-ivory text-sm placeholder:text-ivory/30 outline-none focus:border-blush transition-colors duration-300"
          />
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-[11px] tracking-[0.2em] uppercase text-ivory/50 mb-2">Shape</label>
            <select data-testid="gallery-add-shape" value={newShape} onChange={(e) => setNewShape(e.target.value)} className={selectCls}>
              {SHAPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <button
            data-testid="gallery-add-submit"
            type="submit"
            disabled={adding}
            className="flex items-center gap-2 rounded-full bg-blush text-midnight text-[11px] tracking-[0.2em] uppercase font-medium px-5 py-3 hover:bg-gold transition-colors duration-300 disabled:opacity-50"
          >
            <Plus size={15} /> {adding ? "Adding…" : "Add"}
          </button>
        </div>
      </form>

      {items === null && <p className="text-ivory/50">Loading…</p>}
      {items && items.length === 0 && <p className="text-ivory/50 font-light">No photos yet — add your first above.</p>}

      <p className="text-[11px] tracking-[0.2em] uppercase text-ivory/40 mb-4">Drag photos to reorder · changes appear on the live site instantly</p>
      <div className="space-y-4">
        {items?.map((it, i) => (
          <div
            key={it.id}
            data-testid={`gallery-item-row-${i}`}
            draggable
            onDragStart={() => { dragIndex.current = i; }}
            onDragOver={(e) => onDragOver(e, i)}
            onDragEnd={persistOrder}
            className="flex flex-col sm:flex-row sm:items-center gap-5 bg-ivory/5 border border-ivory/10 rounded-2xl p-4 sm:p-5"
          >
            <div className="flex items-center gap-3 shrink-0">
              <span className="cursor-grab active:cursor-grabbing text-ivory/40 hover:text-ivory" title="Drag to reorder">
                <GripVertical size={18} />
              </span>
              <img
                src={resolveSrc(it.image_url)}
                alt={it.description}
                className="w-24 h-24 object-cover rounded-xl border border-ivory/10"
              />
            </div>

            <div className="flex-1 min-w-0">
              <label className="block text-[10px] tracking-[0.2em] uppercase text-ivory/40 mb-1">Description</label>
              <input
                data-testid={`gallery-desc-input-${i}`}
                value={it.description}
                onChange={(e) => setLocalDesc(it.id, e.target.value)}
                onBlur={(e) => saveField(it.id, { description: e.target.value })}
                placeholder="Add a caption…"
                className="w-full bg-transparent border-b border-ivory/20 py-2 text-ivory text-sm placeholder:text-ivory/30 outline-none focus:border-blush transition-colors duration-300"
              />
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div>
                <label className="block text-[10px] tracking-[0.2em] uppercase text-ivory/40 mb-1">Shape</label>
                <select
                  data-testid={`gallery-shape-select-${i}`}
                  value={it.shape}
                  onChange={(e) => saveField(it.id, { shape: e.target.value })}
                  className={selectCls}
                >
                  {SHAPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <label
                data-testid={`gallery-replace-btn-${i}`}
                className="flex items-center gap-2 rounded-full border border-ivory/25 text-ivory/70 text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 hover:border-blush hover:text-blush transition-colors duration-300 cursor-pointer"
              >
                <ImageUp size={14} /> Replace
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => { replaceImage(it.id, e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
              <button
                data-testid={`gallery-delete-btn-${i}`}
                onClick={() => remove(it.id)}
                className="w-10 h-10 rounded-full border border-ivory/20 text-ivory/60 hover:border-coral hover:text-coral transition-colors duration-300 flex items-center justify-center"
                title="Delete photo"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
